import { supabase } from '../../integrations/supabase/client';
import type { IWebhookRepository, Webhook, EventLog } from '../interfaces/IWebhookRepository';

export class SupabaseWebhookRepository implements IWebhookRepository {
  async getAll(platformTenantId?: string): Promise<Webhook[]> {
    let query = supabase.from('webhooks').select('*').order('name');

    if (platformTenantId) {
      query = query.eq('platform_tenant_id', platformTenantId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || []) as unknown as Webhook[];
  }

  async getById(id: string): Promise<Webhook | null> {
    const { data, error } = await supabase
      .from('webhooks')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data as unknown as Webhook | null;
  }

  async create(data: {
    platform_tenant_id: string | null;
    tenant_id: string;
    name: string;
    url: string;
    events: string[];
    secret: string;
    headers?: any;
  }): Promise<Webhook> {
    const { data: webhook, error } = await supabase
      .from('webhooks')
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return webhook as unknown as Webhook;
  }

  async update(id: string, updates: Partial<Webhook>): Promise<Webhook> {
    const { data, error } = await supabase
      .from('webhooks')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as unknown as Webhook;
  }

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('webhooks')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  async getEventLogs(filters?: {
    platform_tenant_id?: string;
    event_type?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<EventLog[]> {
    let query = supabase
      .from('event_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(100);

    if (filters?.platform_tenant_id) {
      query = query.eq('platform_tenant_id', filters.platform_tenant_id);
    }
    if (filters?.event_type) {
      query = query.eq('event_type', filters.event_type);
    }
    if (filters?.start_date) {
      query = query.gte('created_at', filters.start_date);
    }
    if (filters?.end_date) {
      query = query.lte('created_at', filters.end_date);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || []).map((log: any) => ({
      ...log,
      tenant_id: log.client_id,  // Map client_id to tenant_id for backward compatibility
    })) as EventLog[];
  }

  async logEvent(data: {
    event_type: string;
    event_source: string;
    platform_tenant_id: string | null;
    user_id: string | null;
    payload: any;
    metadata?: any;
  }): Promise<EventLog> {
    const { data: log, error } = await supabase
      .from('event_logs')
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return {
      ...log,
      tenant_id: log.client_id,  // Map client_id to tenant_id for backward compatibility
    } as EventLog;
  }
}
